import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Button,
  Stack,
  RadioGroup,
  Radio,
} from '@mui/joy';
import { ArrowDown as ArrowDownIcon } from '@phosphor-icons/react';
import type { AutomationAction, AutomationModifier, AutomationTrigger } from '../../types/automations';

interface MovePositionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newPosition: number) => void;
  currentItem: AutomationAction | AutomationModifier;
  workflowItems: Array<{
    item: AutomationAction | AutomationModifier | AutomationTrigger;
    type: 'action' | 'modifier' | 'trigger';
    order: number;
  }>;
  moveAll?: boolean;
}

export default function MovePositionModal({ 
  open, 
  onClose, 
  onConfirm, 
  currentItem, 
  workflowItems,
  moveAll = false 
}: MovePositionModalProps) {
  const currentOrder = currentItem.order || 0;
  const [selectedPosition, setSelectedPosition] = useState<number>(currentOrder);

  const getItemLabel = (item: AutomationAction | AutomationModifier | AutomationTrigger, type: string) => {
    if (type === 'trigger') {
      const trigger = item as AutomationTrigger;
      const triggerLabels: Record<string, string> = {
        opportunity_stage_changed: 'Opportunity Stage Changed',
        job_stage_changed: 'Job Stage Changed',
        new_contact_added: 'New Contact Added',
        form_submitted: 'Form Submitted',
        payment_received: 'Payment Received',
        // Add more as needed
      };
      return triggerLabels[trigger.trigger_type] || trigger.trigger_type || 'Trigger';
    } else if (type === 'action') {
      const action = item as AutomationAction;
      const actionLabels: Record<string, string> = {
        send_email: 'Send Email',
        create_notification: 'Create Notification',
        create_task: 'Create Task',
        send_sms: 'Send SMS',
        // Add more as needed
      };
      return actionLabels[action.action_type] || action.action_type;
    } else if (type === 'modifier') {
      const modifier = item as AutomationModifier;
      const modifierLabels: Record<string, string> = {
        wait: 'Wait',
        end: 'End Workflow',
        if_else: 'If/Else',
      };
      return modifierLabels[modifier.modifier_type] || modifier.modifier_type;
    }
    return 'Unknown';
  };

  const handleConfirm = () => {
    onConfirm(selectedPosition);
    onClose();
  };

  // Generate position options
  const positionOptions = [];
  
  // Add "After trigger" option
  {
    positionOptions.push({
      position: 0,
      label: 'After Trigger (First position)',
    });
  }

  // Add positions after each workflow item
  workflowItems
    .filter(w => w.type !== 'trigger')
    .forEach((workflowItem) => {
      // Skip the current item and items that would be moved with it
      if (moveAll) {
        if (workflowItem.order >= currentOrder) return;
      } else {
        if (workflowItem.order === currentOrder) return;
      }

      positionOptions.push({
        position: workflowItem.order + 1,
        label: `After "${getItemLabel(workflowItem.item, workflowItem.type)}"`,
      });
    });

  // Add "At end" option
  const maxOrder = Math.max(...workflowItems.filter(w => w.type !== 'trigger').map(w => w.order));
  if (selectedPosition !== maxOrder + 1) {
    positionOptions.push({
      position: maxOrder + 1,
      label: 'At End (Last position)',
    });
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="md">
        <ModalClose />
        <Typography level="h4">
          {moveAll ? 'Move All From Here' : 'Move Item'}
        </Typography>
        <Typography level="body-sm" sx={{ mb: 2 }}>
          {moveAll 
            ? 'Select where to move this item and all items after it'
            : 'Select a new position for this item'
          }
        </Typography>

        <RadioGroup
          value={selectedPosition}
          onChange={(event) => setSelectedPosition(Number(event.target.value))}
        >
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {positionOptions.map((option) => (
              <ListItem key={option.position}>
                <ListItemButton>
                  <Radio value={option.position} />
                  <ListItemContent>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ArrowDownIcon size={18} color="var(--joy-palette-text-secondary)" />
                      <Typography>{option.label}</Typography>
                    </Stack>
                  </ListItemContent>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </RadioGroup>

        <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="solid" 
            color="primary" 
            onClick={handleConfirm}
            disabled={selectedPosition === currentOrder}
          >
            Move {moveAll && 'All'}
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}